import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Globe2,
  Loader2,
  Pencil,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react';
import { useCompanyWorkspace } from '../components/companyWorkspaceContext';
import { companiesApi, type Company } from '../services/api';
import {
  companyContextApi,
  companyOnboardingApi,
  emptyCompanyProfile,
  isResearching,
  isOlderDraft,
  ContextApiError,
  type CompanyBusinessProfile,
  type CompanyContext,
  type ContextSource,
  type OnboardingCapabilities,
  type OnboardingDraft,
} from '../company-context/api';
import { BusinessBrief, ProfileEditor, profileFields } from '../company-context/ProfileEditor';
import SourceLibrary from '../company-context/SourceLibrary';
import ResearchProgress from '../company-context/ResearchProgress';
import { CompanyOnboardingWizard } from './CompanySetupPage';
import '../company-context/CompanyContext.css';

export default function CompanyContextPage() {
  const { companyId = '' } = useParams();
  const workspace = useCompanyWorkspace();
  const [company, setCompany] = useState<Company | null>(workspace?.company || null);
  const [capabilities, setCapabilities] = useState<OnboardingCapabilities | null>(null);
  const [context, setContext] = useState<CompanyContext | null>(null);
  const [profile, setProfile] = useState<CompanyBusinessProfile>({ ...emptyCompanyProfile });
  const [website, setWebsite] = useState('');
  const [pendingDraft, setPendingDraft] = useState<OnboardingDraft | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [attempt, setAttempt] = useState(0);
  const currentContext = useRef(context);
  const mounted = useRef(true);
  const generation = useRef(0);
  const pollingError = useRef<string | null>(null);
  const actionError = useRef(false);
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const canEdit =
    company?.capabilities?.editOrganisation ?? ['owner', 'admin'].includes(company?.role || '');
  const acceptContext = (next: CompanyContext, expectedGeneration = generation.current) => {
    if (
      !mounted.current ||
      generation.current !== expectedGeneration ||
      next.companyId !== companyId
    )
      return;
    if (
      currentContext.current?.companyId === next.companyId &&
      currentContext.current.revision > next.revision
    )
      return;
    const previous = currentContext.current;
    currentContext.current = next;
    setWebsite((value) =>
      !previous || value === previous.profile.website ? next.profile.website : value
    );
    setContext(next);
    if (!editingRef.current) setProfile({ ...emptyCompanyProfile, ...next.profile });
    workspace?.updateCompanyAppearance({ name: next.profile.name, logo: company?.logo });
  };
  const refresh = async (expectedGeneration = generation.current) => {
    const next = await companyContextApi.get(companyId);
    if (!mounted.current || generation.current !== expectedGeneration) return next;
    acceptContext(next, expectedGeneration);
    if (next.activeDraftId) {
      try {
        const draft = await companyOnboardingApi.get(next.activeDraftId);
        if (mounted.current && generation.current === expectedGeneration)
          setPendingDraft(draft.status === 'completed' ? null : draft);
      } catch (err) {
        if (!(err instanceof ContextApiError) || ![403, 404].includes(err.status)) throw err;
        if (mounted.current && generation.current === expectedGeneration) setPendingDraft(null);
      }
    } else if (mounted.current && generation.current === expectedGeneration) setPendingDraft(null);
    return next;
  };
  const acceptDraft = (next: OnboardingDraft, expectedGeneration: number) => {
    if (
      !mounted.current ||
      generation.current !== expectedGeneration ||
      next.companyId !== companyId
    )
      return;
    setPendingDraft((current) => (isOlderDraft(current, next) ? current : next));
  };
  useEffect(() => {
    mounted.current = true;
    const expectedGeneration = ++generation.current;
    setError('');
    setNotice('');
    setBusy(false);
    actionError.current = false;
    pollingError.current = null;
    setContext(null);
    currentContext.current = null;
    setPendingDraft(null);
    setWebsite('');
    setReviewing(false);
    setEditing(false);
    Promise.all([companyOnboardingApi.capabilities(), companiesApi.get(companyId)])
      .then(async ([flags, result]) => {
        if (!mounted.current || generation.current !== expectedGeneration) return;
        setCapabilities(flags);
        if (result.success && result.data) setCompany(result.data);
        if (flags.enabled) await refresh(expectedGeneration);
      })
      .catch((err) => {
        if (mounted.current && generation.current === expectedGeneration) setError(err.message);
      });
    return () => {
      mounted.current = false;
    };
  }, [companyId, attempt]);
  useEffect(() => {
    if (!isResearching(pendingDraft) || reviewing) return;
    let active = true;
    const expectedGeneration = generation.current;
    const timer = setInterval(() => {
      if (!pendingDraft) return;
      companyOnboardingApi
        .get(pendingDraft.id)
        .then(async (next) => {
          if (active) {
            if (!isResearching(next)) await refresh(expectedGeneration);
            if (!active) return;
            acceptDraft(next, expectedGeneration);
            if (pollingError.current) {
              const previousError = pollingError.current;
              setError((current) => (current === previousError ? '' : current));
              pollingError.current = null;
            }
          }
        })
        .catch((err) => {
          if (active && !actionError.current) {
            pollingError.current =
              err.message === 'Failed to fetch'
                ? 'We couldn’t reach StratPlan. We’ll retry while your draft stays saved.'
                : err.message;
            setError(pollingError.current!);
          }
        });
    }, 1800);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [pendingDraft?.id, pendingDraft?.status, reviewing]);
  const perform = async (
    action: (current: CompanyContext, expectedGeneration: number) => Promise<void | CompanyContext>,
    message: string | (() => string)
  ) => {
    if (!currentContext.current) return;
    setBusy(true);
    setError('');
    setNotice('');
    actionError.current = false;
    const expectedGeneration = generation.current;
    try {
      const next = await action(currentContext.current, expectedGeneration);
      if (next) acceptContext(next, expectedGeneration);
      if (mounted.current && generation.current === expectedGeneration)
        setNotice(typeof message === 'function' ? message() : message);
    } catch (err) {
      if (mounted.current && generation.current === expectedGeneration) {
        actionError.current = true;
        setError(err instanceof Error ? err.message : 'Unable to update company context.');
      }
    } finally {
      if (mounted.current && generation.current === expectedGeneration) setBusy(false);
    }
  };
  const buildFromWebsite = () =>
    perform(async (current, expectedGeneration) => {
      const next = await companyContextApi.research(companyId, current.revision, website.trim());
      await refresh(expectedGeneration);
      acceptDraft(next, expectedGeneration);
      if (mounted.current && generation.current === expectedGeneration)
        setWebsite(next.profile.website);
    }, 'We’re reading your website and automatically filling in your company brief.');
  const buildFromDocuments = async (files: File[]) => {
    let manualReason = '';
    await perform(
      async (current, expectedGeneration) => {
        let next: OnboardingDraft | null = null;
        try {
          next = await companyContextApi.buildFromDocuments(
            companyId,
            current.revision,
            files,
            website.trim() !== current.profile.website ? website.trim() || undefined : undefined
          );
        } catch (err) {
          if (!(err instanceof ContextApiError) || err.status !== 402) throw err;
          manualReason = err.message;
          for (const file of files) {
            const latest = await companyContextApi.get(companyId);
            await companyContextApi.upload(companyId, latest.revision, file);
            await refresh(expectedGeneration);
          }
        }
        await refresh(expectedGeneration);
        if (next) acceptDraft(next, expectedGeneration);
      },
      () =>
        manualReason
          ? `Documents saved in your private library. ${manualReason}`
          : 'Documents added. We’re reading them and automatically filling in your company brief.'
    );
  };
  const draftStale =
    pendingDraft?.progress?.approvedRevision !== undefined &&
    Number(pendingDraft.progress.approvedRevision) !== context?.revision;
  const draftReady =
    pendingDraft?.status === 'ready' &&
    !draftStale &&
    pendingDraft.researchRuns > 0 &&
    !pendingDraft.progress?.extractOnly &&
    (!pendingDraft.progress?.analysisStatus || pendingDraft.progress.analysisStatus === 'complete');
  const displayedBrief = draftReady ? pendingDraft : context;
  const displayedUnknowns = (context?.unknowns || []).filter((unknown) => {
    const value = displayedBrief?.profile[unknown as keyof CompanyBusinessProfile];
    return Array.isArray(value) ? !value.length : !value;
  });
  const importBusy = busy || editing || isResearching(pendingDraft);
  const briefFormId = `company-context-brief-${companyId}`;
  const validProfile = profile.name.trim().length >= 2 && !!profile.description.trim();
  if (capabilities && !capabilities.enabled)
    return (
      <div className="company-context-page">
        <section className="context-panel">
          <h1>Company context</h1>
          <p>
            The new company brief is not available yet. Your company details are available in
            company settings.
          </p>
          <Link className="context-button" to={`/company/${companyId}/admin`}>
            Company settings <ArrowRight size={16} />
          </Link>
        </section>
      </div>
    );
  if (!context)
    return (
      <div className="company-context-page">
        <section className="context-panel context-loading">
          {error ? (
            <>
              <h1>Let’s reconnect your company brief.</h1>
              <p role="alert">{error}</p>
              <button
                type="button"
                className="context-button"
                onClick={() => setAttempt((value) => value + 1)}
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <Loader2 size={26} className="context-spin" />
              <p role="status">Loading company context…</p>
            </>
          )}
        </section>
      </div>
    );
  if (reviewing && pendingDraft)
    return (
      <>
        <div className="context-review-bar">
          <p>
            <Sparkles size={17} /> Reviewing an updated company brief. Your approved context stays
            active until you approve this version.
          </p>
          <button
            type="button"
            className="context-text-button"
            onClick={() => {
              setReviewing(false);
              void refresh();
            }}
          >
            <X size={16} /> Return to company context
          </button>
        </div>
        <CompanyOnboardingWizard
          key={pendingDraft.id}
          capabilities={capabilities!}
          initialDraft={pendingDraft}
          onApprove={async (draft) => {
            const expectedGeneration = generation.current;
            const approved = await companyContextApi.approve(companyId, draft.id, draft.revision);
            if (!mounted.current || generation.current !== expectedGeneration) return;
            acceptContext(approved, expectedGeneration);
            setPendingDraft(null);
            setReviewing(false);
            setNotice(
              'Updated company brief approved. Future AI suggestions will use this context.'
            );
          }}
        />
      </>
    );
  return (
    <div className="company-context-page">
      <header className="context-page-heading">
        <div>
          <p className="context-eyebrow">The understanding behind your strategy</p>
          <h1>Company context</h1>
          <p>
            A clear, specific picture of {context.profile.name || company?.name || 'your company'},
            kept together with the sources that support it.
          </p>
        </div>
        {canEdit && (
          <div className="context-actions">
            <button
              type="button"
              className="context-button context-button-secondary"
              disabled={busy}
              onClick={() => {
                setEditing(!editing);
                setProfile(context.profile);
              }}
            >
              <Pencil size={16} />{' '}
              {editing
                ? 'Cancel editing'
                : context.revision > 0
                  ? 'Edit company brief'
                  : 'Review & save company brief'}
            </button>
            {editing && (
              <button
                type="submit"
                form={briefFormId}
                className="context-button"
                disabled={busy || !validProfile}
              >
                {busy ? <Loader2 size={16} className="context-spin" /> : <CheckCircle2 size={16} />}{' '}
                Save changes
              </button>
            )}
            {!editing &&
              (context.profile.website ||
                context.sources.some((source) => source.includedInContext)) && (
                <button
                  type="button"
                  className="context-button"
                  disabled={busy || isResearching(pendingDraft) || editing}
                  onClick={() =>
                    void perform(async (current, expectedGeneration) => {
                      const next = await companyContextApi.research(
                        companyId,
                        current.revision,
                        current.profile.website || undefined
                      );
                      acceptDraft(next, expectedGeneration);
                    }, 'Research started. Review and approve the refreshed brief when it is ready.')
                  }
                >
                  <RefreshCw size={16} /> Refresh company brief
                </button>
              )}
          </div>
        )}
      </header>
      {error && (
        <p className="context-error context-page-error" role="alert">
          {error}
          {canEdit && /changed|refresh/i.test(error) && (
            <button
              type="button"
              onClick={() =>
                void perform(async (_current, expectedGeneration) => {
                  await refresh(expectedGeneration);
                  if (mounted.current && generation.current === expectedGeneration)
                    setEditing(false);
                }, 'Saved company brief reloaded.')
              }
            >
              Reload saved brief
            </button>
          )}
        </p>
      )}
      {notice && (
        <p className="context-success context-page-error" role="status">
          <CheckCircle2 size={17} />
          {notice}
        </p>
      )}
      {canEdit && (
        <section
          className="context-panel context-import-panel"
          aria-labelledby="context-import-heading"
        >
          <div className="context-panel-heading">
            <div>
              <p className="context-eyebrow">Let us fill in the details</p>
              <h2 id="context-import-heading">
                <Sparkles size={20} /> Build your company context
              </h2>
            </div>
          </div>
          <p className="context-import-intro">
            Add your website or company documents. We’ll read them and automatically fill in your
            business details. Review your company brief when it’s ready.
          </p>
          <div className="context-import-grid">
            <form
              className="context-website-import"
              onSubmit={(event) => {
                event.preventDefault();
                if (website.trim() && !importBusy) void buildFromWebsite();
              }}
            >
              <label htmlFor="context-company-website">
                <Globe2 size={17} /> Company website
              </label>
              <div className="context-website-input-row">
                <input
                  id="context-company-website"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  placeholder="yourcompany.com"
                  inputMode="url"
                  autoComplete="url"
                  maxLength={2048}
                  disabled={importBusy}
                  aria-describedby="context-website-help"
                />
                <button
                  type="submit"
                  className="context-button"
                  disabled={importBusy || !website.trim()}
                >
                  {isResearching(pendingDraft) ? (
                    <Loader2 size={16} className="context-spin" />
                  ) : (
                    <Sparkles size={16} />
                  )}
                  Build from website
                </button>
              </div>
              <p id="context-website-help" className="context-help">
                We’ll find and read the useful pages for you. No need to fill in every field.
              </p>
              <p className="context-import-review-note">
                <CheckCircle2 size={16} /> Review once before using the updated brief in AI tools.
              </p>
            </form>
            <div className="context-document-import">
              <h3>
                <FileText size={17} /> Or add documents
              </h3>
              <SourceLibrary
                sources={context.sources}
                capabilities={capabilities}
                busy={importBusy}
                showSources={false}
                onUpload={buildFromDocuments}
              />
            </div>
          </div>
        </section>
      )}
      {pendingDraft && (
        <div className="context-research-status" role="status">
          {isResearching(pendingDraft) ? (
            <Loader2 size={21} className="context-spin" />
          ) : (
            <Sparkles size={21} />
          )}
          <div>
            <strong>
              {isResearching(pendingDraft)
                ? 'Updating our understanding of your company'
                : draftStale
                  ? 'Your saved brief changed. Build again to include the latest details.'
                  : draftReady
                    ? 'Your company brief is filled in and ready to review'
                    : pendingDraft.status === 'failed'
                      ? 'We couldn’t finish building your company brief'
                      : 'Your sources are saved. Build your company brief when you’re ready.'}
            </strong>
            <p>
              {draftReady
                ? 'The automatically filled draft is shown below. Review and approve it to use these details in future AI suggestions.'
                : 'Your sources and current brief stay saved. You can return here while we fill in the details.'}
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              className="context-button context-button-secondary"
              disabled={busy || isResearching(pendingDraft)}
              onClick={() => {
                if (!draftReady)
                  void perform(async (current, expectedGeneration) => {
                    const next = await companyContextApi.research(companyId, current.revision);
                    acceptDraft(next, expectedGeneration);
                  }, 'Building an updated brief from your company sources.');
                else setReviewing(true);
              }}
            >
              {draftReady ? 'Review & use this brief' : 'Build company brief'}{' '}
              <ArrowRight size={15} />
            </button>
          )}
        </div>
      )}
      {pendingDraft && pendingDraft.warnings?.length > 0 && (
        <div className="context-import-warnings" role="status">
          {pendingDraft.warnings.map((warning, index) => (
            <p key={index}>{warning}</p>
          ))}
        </div>
      )}
      {pendingDraft && isResearching(pendingDraft) && (
        <ResearchProgress
          draft={pendingDraft}
          busy={busy}
          onCancel={
            canEdit
              ? () =>
                  void perform(async (_current, expectedGeneration) => {
                    const next = await companyOnboardingApi.cancel(
                      pendingDraft.id,
                      pendingDraft.revision
                    );
                    acceptDraft(next, expectedGeneration);
                    if (mounted.current && generation.current === expectedGeneration)
                      setReviewing(true);
                  }, 'Research stopped. You can continue editing the brief manually.')
              : undefined
          }
        />
      )}
      <div className="context-company-grid">
        <div>
          {editing ? (
            <form
              id={briefFormId}
              className="context-panel"
              onSubmit={(event) => {
                event.preventDefault();
                void perform(async (current, expectedGeneration) => {
                  const saved = await companyContextApi.save(companyId, current.revision, profile);
                  if (mounted.current && generation.current === expectedGeneration)
                    setEditing(false);
                  return saved;
                }, 'Company brief saved. Future AI suggestions will use this context.');
              }}
            >
              <div className="context-panel-heading">
                <div>
                  <p className="context-eyebrow">Editing company brief</p>
                  <h2>
                    {context.revision > 0
                      ? 'Keep the details accurate.'
                      : 'Review and save your company brief.'}
                  </h2>
                </div>
              </div>
              <ProfileEditor
                profile={profile}
                onChange={setProfile}
                facts={context.facts}
                sources={context.sources}
                disabled={busy}
              />
              <div className="context-actions">
                <button type="submit" className="context-button" disabled={busy || !validProfile}>
                  {busy ? (
                    <Loader2 size={16} className="context-spin" />
                  ) : (
                    <CheckCircle2 size={16} />
                  )}{' '}
                  Save company brief
                </button>
                <button
                  type="button"
                  className="context-text-button"
                  disabled={busy}
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              {draftReady && (
                <p className="context-draft-label">
                  <Sparkles size={16} /> Automatically filled draft · awaiting your review
                </p>
              )}
              <BusinessBrief
                profile={displayedBrief!.profile}
                facts={displayedBrief!.facts}
                sources={displayedBrief!.sources}
                editable={false}
                reviewState={!draftReady && context.revision > 0 ? 'saved' : 'draft'}
              />
            </>
          )}
          {displayedUnknowns.length > 0 && (
            <section className="context-panel context-unknowns">
              <h2>Details still to understand</h2>
              <p>These gaps stay unknown until you add reliable information.</p>
              <ul>
                {displayedUnknowns.map((unknown, index) => (
                  <li key={index}>
                    {profileFields.find((item) => item.field === unknown)?.label ||
                      (
                        {
                          name: 'Company name',
                          industry: 'Industry',
                          website: 'Website',
                          size: 'Company size',
                        } as Record<string, string>
                      )[unknown] ||
                      unknown}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
        <section className="context-panel context-company-sources">
          <div className="context-panel-heading">
            <div>
              <p className="context-eyebrow">Private company library</p>
              <h2>
                <FileText size={20} /> Sources & documents
              </h2>
            </div>
          </div>
          <p className="context-help">
            The website pages and private documents behind your company brief.
          </p>
          {context.sources.length === 0 && (
            <p className="context-help">
              Your imported website pages and documents will appear here.
            </p>
          )}
          <SourceLibrary
            sources={context.sources}
            capabilities={capabilities}
            busy={importBusy}
            showUpload={false}
            readOnly={!canEdit}
            onUpload={buildFromDocuments}
            onRemove={async (source) => {
              await perform(
                (current) => companyContextApi.removeSource(companyId, source.id, current.revision),
                'Source removed from this company’s library and context.'
              );
            }}
            onInclude={async (source, included) => {
              await perform(
                (current) =>
                  companyContextApi.includeSource(companyId, source.id, current.revision, included),
                included
                  ? 'Source included in company context.'
                  : 'Source excluded from company context.'
              );
            }}
            onReplace={async (source, file) => {
              let manualReason = '';
              await perform(
                async (current, expectedGeneration) => {
                  await companyContextApi.replaceSource(
                    companyId,
                    source.id,
                    current.revision,
                    file
                  );
                  const latest = await refresh(expectedGeneration);
                  try {
                    const next = await companyContextApi.research(companyId, latest.revision);
                    acceptDraft(next, expectedGeneration);
                  } catch (err) {
                    if (err instanceof ContextApiError && err.status === 402)
                      manualReason = err.message;
                    else
                      throw new Error(
                        `Your document was replaced and saved, but we couldn’t rebuild the brief. ${err instanceof Error ? err.message : 'Please try again.'}`
                      );
                  }
                },
                () =>
                  manualReason
                    ? `Document replaced and saved in your private library. ${manualReason}`
                    : 'Document replaced. We’re filling in an updated company brief.'
              );
            }}
            onDownload={async (source: ContextSource) => {
              await companyContextApi.download(companyId, source);
            }}
          />
        </section>
      </div>
    </div>
  );
}
